import { useEffect, useMemo, useState } from "react";
import { IntroScene } from "./IntroScene";
import { previewDate, sceneFor } from "../../lib/season";
import { api } from "../../lib/api";

// Apple-style opening: a dark curtain, the greeting blooms in (blur → crisp), holds a beat, then the curtain dissolves to the workspace.
// Scenery = the owner's pick (Owner dashboard → Intro → Opening scene: Auto seasons / Studio Blue / Night Sky / Off, from /api/services,
// last value cached per device so the first frame is right) — lib/season.js. Signed in: "Good morning, Taji" once per app open
// (sessionStorage). Fresh install, nobody signed in yet: "Welcome to Xpattern" once per device (localStorage). Skippable with a click or
// any key, instant under prefers-reduced-motion. `preview` = the owner trying a scene from the dashboard: plays once, touches no storage.
const KEY = "xpattern.introShown", WELCOMED = "xpattern.welcomed", SCENE_KEY = "xpattern.introScene";
const HOLD_MS = 3300, OUT_MS = 950;  // a touch slower: the greeting holds ~3.3 s, then a slow dissolve into the workspace

export const greetingFor = (d = new Date()) => { const h = d.getHours(); return h < 5 ? "Working late" : h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };
export const firstNameOf = (user) => user.role === "admin" ? "Taji" : (user.name || "").trim().split(/\s+/)[0] || (user.shop_name || "").trim() || (user.email || "").split("@")[0];
const cachedScene = () => { try { return localStorage.getItem(SCENE_KEY) || "auto"; } catch { return "auto"; } };

export const WelcomeIntro = ({ user, welcome = false, preview = null, onDone }) => {
  const [phase, setPhase] = useState(() => (preview ? "in" : sessionStorage.getItem(KEY) || (welcome && localStorage.getItem(WELCOMED)) ? "done" : "in"));
  const [scene, setScene] = useState(() => preview || cachedScene());
  useEffect(() => {
    if (preview || phase === "done") return;
    api.services().then((r) => { const s = r?.scene || "auto"; setScene(s); try { localStorage.setItem(SCENE_KEY, s); } catch {} }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (phase === "done") return undefined;
    if (!preview) { sessionStorage.setItem(KEY, "1"); if (welcome) localStorage.setItem(WELCOMED, "1"); }
    const quick = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const out = setTimeout(() => setPhase("out"), quick ? 900 : HOLD_MS);
    return () => clearTimeout(out);
  }, [phase === "done"]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (phase === "in") return undefined;
    if (user && !preview) { window.__xpIntroDone = true; window.dispatchEvent(new Event("xpattern:intro-done")); }  // the service hub waits for the signed-in greeting to start dissolving
    if (phase === "done") { onDone?.(); return undefined; }
    const t = setTimeout(() => setPhase("done"), OUT_MS);
    return () => clearTimeout(t);
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (phase !== "in") return undefined;
    const skip = () => setPhase("out");
    window.addEventListener("keydown", skip);
    return () => window.removeEventListener("keydown", skip);
  }, [phase]);
  const today = useMemo(() => previewDate() || new Date(), []);
  const season = useMemo(() => sceneFor(scene, today), [scene, today]);
  if (phase === "done" || (!user && !welcome)) return null;
  const first = user ? firstNameOf(user) : "";
  const place = !user ? "" : [(user.city || "").trim(), (user.state || "").trim()].filter(Boolean).join(", ")
    || ((user.address || "").trim().match(/^[A-Za-z .'-]{2,40},\s*[A-Za-z]{2}$/) ? user.address.trim() : "");  // the customer's City, State from sign-up (or a short "City, ST" address)
  const date = today.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const vars = { "--intro-bg": season.bg, "--intro-glow": season.glow, "--intro-n1": season.name[0], "--intro-n2": season.name[1], "--intro-n3": season.name[2], "--intro-sub": season.sub };
  return (
    <div className={`intro-curtain ${phase === "out" ? "is-out" : ""} ${season.ice ? "is-ice" : ""}`} style={vars} onClick={() => setPhase("out")} data-testid="welcome-intro" role="presentation" data-season={season.id} data-scene={scene} data-preview={preview || undefined}>
      {season.effect !== "none" && <IntroScene season={season} />}
      <img src="/xpattern-x.png" alt="" className="intro-mark" draggable="false" />
      <h1 className="intro-greeting" data-testid="welcome-intro-greeting">
        {user
          ? <><span className="intro-word">{greetingFor()},</span>{" "}<span className="intro-name" data-testid="welcome-intro-name">{first}</span></>
          : <><span className="intro-word">Welcome to</span>{" "}<span className="intro-name" data-testid="welcome-intro-name">Xpattern</span></>}
      </h1>
      <p className="intro-sub" data-testid="welcome-intro-sub">{user ? `${date}${place ? ` · ${place}` : ""}` : "Precision tint patterns · Let’s get you set up"}</p>
      {user && season.holiday && <p className="intro-holiday" style={{ "--intro-accent": season.holiday.accent }} data-testid="welcome-intro-holiday">{season.holiday.line}</p>}
    </div>
  );
};
